import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Clock, Users, Home, Settings, LogOut, Menu as MenuIcon, ChevronsUpDown, Sparkles } from 'lucide-react';
import { supabase } from './services/supabase';
import LandingPage from './components/LandingPage';
import JoinWithInvite from './components/JoinWithInvite';
import Auth from './components/Auth';
import CreateProjectFlow from './components/CreateProjectFlow';
import UpdatePassword from './components/UpdatePassword';
import PrivacyPolicy from './components/PrivacyPolicy';
import About from './components/About';
import Dashboard from './components/Dashboard';
import Membros from './components/Membros';
import Tarefas from './components/Tarefas';
import Insights from './components/Insights';
import Config from './components/Config';
import FormModal from './components/FormModal';
import { useI18n } from './i18n';
import { exec, fetchAllRows, sortRegistros, chunk } from './utils';
import { uploadAvatar, removeAvatar, memberAvatarPath } from './lib/images';
import { Avatar, Button, IconButton, Menu, Notice, cx } from './components/ui';

const EMPTY_DATA = { membros: [], tarefas: [], registros: [], categorias: [] };
const EMPTY_PERFIL = { nome: '', avatar_url: null };
const ARCHIVE_BATCH = 200;

export default function BancoDeHoras() {
  const { t } = useI18n();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  // null = not resolved yet, false = the user has no project
  const [projetoId, setProjetoId] = useState(null);
  const [projetoErro, setProjetoErro] = useState(false);
  const [projeto, setProjeto] = useState(null);
  const [data, setData] = useState(EMPTY_DATA);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState(null);
  const [welcomeFlow, setWelcomeFlow] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [perfil, setPerfil] = useState(EMPTY_PERFIL);
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  const loadSeq = useRef(0);

  const userId = session?.user?.id || null;

  const notify = useCallback((text, isError = true) => {
    clearTimeout(noticeTimer.current);
    setNotice({ text, isError });
    noticeTimer.current = setTimeout(() => setNotice(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => setSession(session))
      .catch((error) => console.error('Error restoring session:', error))
      .finally(() => setLoading(false));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        return;
      }
      // Supabase re-announces the same session every time the tab regains
      // focus; keep the existing object so nothing re-renders or refetches.
      setSession(prev => (prev?.access_token === next?.access_token ? prev : next));
    });

    return () => subscription.unsubscribe();
  }, []);

  const inicializarProjeto = useCallback(async () => {
    setProjetoErro(false);
    const { data: usuarioProjetos, error } = await exec(
      supabase.from('usuarios_projetos').select('projeto_id').eq('user_id', userId).order('projeto_id').limit(1)
    );
    if (error) {
      console.error('Error initializing project:', error);
      setProjetoErro(true);
      return;
    }
    setProjetoId(usuarioProjetos && usuarioProjetos.length > 0 ? usuarioProjetos[0].projeto_id : false);
  }, [userId]);

  const carregarDados = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoadError(false);
    const doProjeto = (tabela, ordem) => fetchAllRows((from, to) => {
      let query = supabase.from(tabela).select('*', { count: 'exact' }).eq('projeto_id', projetoId);
      ordem.forEach(([coluna, ascending]) => { query = query.order(coluna, { ascending }); });
      return query.range(from, to);
    });

    const [projetoRes, categoriasRes, membrosRes, tarefasRes, registrosRes] = await Promise.all([
      exec(supabase
        .from('projetos')
        .select('id, nome, descricao, finalidade, finalidade_outro, tem_filhos, tem_animais, proprietario_id, avatar_url')
        .eq('id', projetoId)
        .maybeSingle()),
      exec(supabase.from('categorias').select('*').order('id')),
      doProjeto('membros', [['criado_em', true], ['id', true]]),
      doProjeto('tarefas', [['criado_em', true], ['id', true]]),
      doProjeto('registros', [['criado_em', false], ['id', false]]),
    ]);
    if (seq !== loadSeq.current) return;

    const falha = [projetoRes, categoriasRes, membrosRes, tarefasRes, registrosRes].find(r => r.error);
    if (falha) {
      console.error('Error loading data:', falha.error);
      setLoadError(true);
      return;
    }

    if (projetoRes.data) setProjeto(projetoRes.data);
    setData({
      membros: membrosRes.data || [],
      tarefas: tarefasRes.data || [],
      registros: registrosRes.data || [],
      categorias: categoriasRes.data || []
    });
    setDataLoaded(true);
  }, [projetoId]);

  useEffect(() => {
    if (userId) {
      inicializarProjeto();
      return;
    }
    loadSeq.current += 1;
    setProjetoId(null);
    setProjetoErro(false);
    setProjeto(null);
    setData(EMPTY_DATA);
    setDataLoaded(false);
    setLoadError(false);
    setPerfil(EMPTY_PERFIL);
    setCurrentView('dashboard');
    setModal(null);
  }, [userId, inicializarProjeto]);

  useEffect(() => {
    if (projetoId) {
      carregarDados();
    }
  }, [projetoId, carregarDados]);

  // The profile name and photo feed the sidebar and the activity form's
  // default participant (the one whose name matches the signed-in user).
  useEffect(() => {
    if (!userId) return;
    exec(supabase.from('perfis').select('nome, avatar_url').eq('id', userId).maybeSingle())
      .then(({ data }) => { if (data) setPerfil({ nome: data.nome || '', avatar_url: data.avatar_url || null }); });
  }, [userId]);

  const rememberMembro = useCallback((membroId) => {
    try {
      if (membroId) localStorage.setItem(`dojo-mojo-last-membro-${projetoId}`, String(membroId));
    } catch (_) { /* localStorage may be unavailable */ }
  }, [projetoId]);

  // Default participant for the "log activity" form: last one this browser used,
  // else the participant whose name matches the logged-in profile, else the first.
  const defaultMembroId = useMemo(() => {
    if (!data.membros.length) return '';
    try {
      const remembered = localStorage.getItem(`dojo-mojo-last-membro-${projetoId}`);
      if (remembered && data.membros.some(m => String(m.id) === remembered)) {
        return parseInt(remembered, 10);
      }
    } catch (_) { /* ignore */ }
    if (perfil.nome) {
      const alvo = perfil.nome.trim().toLowerCase();
      const match = data.membros.find(m => (m.nome || '').trim().toLowerCase() === alvo);
      if (match) return match.id;
    }
    return data.membros[0]?.id || '';
  }, [data.membros, perfil.nome, projetoId]);

  const handleLogout = async () => {
    await exec(supabase.auth.signOut());
    setWelcomeFlow(null);
  };

  const openModal = (type, item = null) => setModal({ type, item });
  const closeModal = () => setModal(null);

  // Every write below resolves to true/false. Local state only changes after
  // the server confirms, so a failed save never leaves the screen out of sync.
  const falhou = (contexto, error, mensagem = 'errors.saveFailed') => {
    console.error(contexto, error);
    notify(t(mensagem));
    return false;
  };

  const substituirMembro = (atualizado) => setData(prev => ({ ...prev, membros: prev.membros.map(m => m.id === atualizado.id ? atualizado : m) }));

  // Uploads (or removes) a participant photo after the row itself is saved.
  // Photo problems are reported but do not undo the saved participant.
  const aplicarFotoMembro = async (membro, foto) => {
    if (!foto || (!foto.file && !foto.remove)) return;
    const path = memberAvatarPath(projetoId, membro.id);
    try {
      let avatar_url = null;
      if (foto.file) {
        avatar_url = await uploadAvatar(supabase, path, foto.file);
      } else {
        await removeAvatar(supabase, path).catch(() => {});
      }
      const { data: atualizado, error } = await exec(supabase.from('membros').update({ avatar_url }).eq('id', membro.id).select().single());
      if (error || !atualizado) throw error || new Error('no row');
      substituirMembro(atualizado);
    } catch (error) {
      console.error('Error saving participant photo:', error);
      notify(t('errors.photoFailed'));
    }
  };

  const adicionarMembro = async (nome, genero = null, dataNascimento = null, regimeTrabalho = null, foto = null) => {
    const membroData = { nome, projeto_id: projetoId };
    if (genero && genero !== 'prefiro_nao_declarar') membroData.genero = genero;
    if (dataNascimento) membroData.data_nascimento = dataNascimento;
    if (regimeTrabalho) membroData.regime_trabalho = regimeTrabalho;

    const { data: novoMembro, error } = await exec(supabase.from('membros').insert(membroData).select().single());
    if (error || !novoMembro) return falhou('Error adding member:', error);
    setData(prev => ({ ...prev, membros: [...prev.membros, novoMembro] }));
    await aplicarFotoMembro(novoMembro, foto);
    return true;
  };

  const editarMembro = async (id, nome, genero = null, dataNascimento = null, regimeTrabalho = null, foto = null) => {
    const membroData = {
      nome,
      genero: (genero && genero !== 'prefiro_nao_declarar') ? genero : null,
      data_nascimento: dataNascimento || null,
      regime_trabalho: regimeTrabalho || null
    };

    const { data: membroAtualizado, error } = await exec(
      supabase.from('membros').update(membroData).eq('id', id).eq('projeto_id', projetoId).select().single()
    );
    if (error || !membroAtualizado) return falhou('Error editing member:', error);
    substituirMembro(membroAtualizado);
    await aplicarFotoMembro(membroAtualizado, foto);
    return true;
  };

  // Deletes report the rows they removed; none means the server refused.
  const apagar = async (tabela, id) => {
    const { data: apagados, error } = await exec(supabase.from(tabela).delete().eq('id', id).select('id'));
    if (error || !apagados || apagados.length === 0) {
      return falhou(`Error deleting from ${tabela}:`, error, 'errors.deleteFailed');
    }
    return true;
  };

  const removerMembro = async (membro) => {
    if (!window.confirm(t('members.confirmDelete', { name: membro.nome }))) return false;
    if (!(await apagar('membros', membro.id))) return false;
    if (membro.avatar_url) removeAvatar(supabase, memberAvatarPath(projetoId, membro.id)).catch(() => {});
    setData(prev => ({
      ...prev,
      membros: prev.membros.filter(m => m.id !== membro.id),
      registros: prev.registros.filter(r => r.membro_id !== membro.id)
    }));
    return true;
  };

  const adicionarTarefa = async (nome, categoriaId, peso) => {
    const { data: novaTarefa, error } = await exec(
      supabase.from('tarefas').insert({ nome, categoria_id: categoriaId, peso: parseFloat(peso), projeto_id: projetoId }).select().single()
    );
    if (error || !novaTarefa) return falhou('Error adding task:', error);
    setData(prev => ({ ...prev, tarefas: [...prev.tarefas, novaTarefa] }));
    return true;
  };

  const editarTarefa = async (id, nome, categoriaId, peso) => {
    const { data: tarefaAtualizada, error } = await exec(
      supabase.from('tarefas').update({ nome, categoria_id: categoriaId, peso: parseFloat(peso) }).eq('id', id).select().single()
    );
    if (error || !tarefaAtualizada) return falhou('Error editing task:', error);
    setData(prev => ({ ...prev, tarefas: prev.tarefas.map(tf => tf.id === id ? tarefaAtualizada : tf) }));
    return true;
  };

  const removerTarefa = async (tarefa) => {
    if (!window.confirm(t('tasks.confirmDelete', { name: tarefa.nome }))) return false;
    if (!(await apagar('tarefas', tarefa.id))) return false;
    setData(prev => ({
      ...prev,
      tarefas: prev.tarefas.filter(tf => tf.id !== tarefa.id),
      registros: prev.registros.filter(r => r.tarefa_id !== tarefa.id)
    }));
    return true;
  };

  const registrarAtividade = async (membroId, tarefaId, tempoMinutos, dataRegistro, comentario) => {
    const { data: novoRegistro, error } = await exec(supabase.from('registros').insert({
      membro_id: membroId,
      tarefa_id: tarefaId,
      tempo_minutos: parseInt(tempoMinutos, 10),
      projeto_id: projetoId,
      criado_em: dataRegistro,
      comentario: (comentario || '').trim() || null
    }).select().single());
    if (error || !novoRegistro) return falhou('Error registering activity:', error);
    setData(prev => ({ ...prev, registros: sortRegistros([novoRegistro, ...prev.registros]) }));
    rememberMembro(membroId);
    return true;
  };

  const atualizarRegistro = async (id, membroId, tarefaId, tempoMinutos, dataRegistro, comentario) => {
    const { data: registroAtualizado, error } = await exec(supabase.from('registros').update({
      membro_id: membroId,
      tarefa_id: tarefaId,
      tempo_minutos: parseInt(tempoMinutos, 10),
      criado_em: dataRegistro,
      comentario: (comentario || '').trim() || null
    }).eq('id', id).select().single());
    if (error || !registroAtualizado) return falhou('Error updating record:', error);
    setData(prev => ({ ...prev, registros: sortRegistros(prev.registros.map(r => r.id === id ? registroAtualizado : r)) }));
    rememberMembro(membroId);
    return true;
  };

  const removerRegistro = async (registro) => {
    if (!window.confirm(t('dashboard.confirmDeleteEntry'))) return false;
    if (!(await apagar('registros', registro.id))) return false;
    setData(prev => ({ ...prev, registros: prev.registros.filter(r => r.id !== registro.id) }));
    return true;
  };

  const arquivarHistorico = async () => {
    if (!window.confirm(t('config.archiveConfirm'))) return;

    // Read straight from the server (all pages) so every entry that gets
    // deleted has been archived first, whatever is loaded on screen.
    const { data: todos, error } = await fetchAllRows((from, to) => supabase
      .from('registros')
      .select('*', { count: 'exact' })
      .eq('projeto_id', projetoId)
      .order('id')
      .range(from, to));
    if (error) {
      console.error('Error archiving history:', error);
      alert(t('config.archiveError'));
      return;
    }
    if (todos.length === 0) {
      alert(t('config.archiveNone'));
      return;
    }

    const arquivadoEm = new Date().toISOString();
    let arquivados = 0;
    let falha = null;
    for (const lote of chunk(todos, ARCHIVE_BATCH)) {
      const { error: insertError } = await exec(supabase.from('registros_arquivados').insert(lote.map(r => ({
        membro_id: r.membro_id,
        tarefa_id: r.tarefa_id,
        tempo_minutos: r.tempo_minutos,
        projeto_id: r.projeto_id,
        criado_em: r.criado_em,
        comentario: r.comentario || null,
        arquivado_em: arquivadoEm
      }))));
      if (insertError) { falha = insertError; break; }

      const ids = lote.map(r => r.id);
      const { error: deleteError } = await exec(supabase.from('registros').delete().in('id', ids));
      if (deleteError) { falha = deleteError; break; }

      arquivados += lote.length;
      const removidos = new Set(ids);
      setData(prev => ({ ...prev, registros: prev.registros.filter(r => !removidos.has(r.id)) }));
    }

    if (falha) {
      console.error('Error archiving history:', falha);
      alert(t('config.archiveError'));
      carregarDados();
      return;
    }
    alert(t('config.archiveSuccess', { count: arquivados }));
  };

  const fullScreen = (children) => (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">{children}</div>
  );

  if (loading) return fullScreen(<p className="text-sm font-medium text-gray-500">{t('common.loading')}</p>);

  if (isPasswordRecovery) {
    return <UpdatePassword onSuccess={() => { setIsPasswordRecovery(false); window.location.href = '/'; }} />;
  }

  if (!session) {
    const authScreen = (mode) => (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto p-6">
          <button onClick={() => setWelcomeFlow(null)} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium">← {t('common.back')}</button>
          <Auth initialMode={mode} onJoinProject={() => setWelcomeFlow('join')} />
        </div>
      </div>
    );
    if (!welcomeFlow) return (<LandingPage onLogin={() => setWelcomeFlow('login')} onCreateProject={() => setWelcomeFlow('signup')} onJoinProject={() => setWelcomeFlow('join')} onPrivacyPolicy={() => setWelcomeFlow('privacy')} onAbout={() => setWelcomeFlow('about')} />);
    if (welcomeFlow === 'join') return (<JoinWithInvite onBack={() => setWelcomeFlow(null)} onSuccess={() => { setWelcomeFlow(null); window.location.reload(); }} />);
    if (welcomeFlow === 'login') return authScreen('login');
    if (welcomeFlow === 'signup') return authScreen('signup');
    if (welcomeFlow === 'create') return (<CreateProjectFlow onBack={() => setWelcomeFlow(null)} onSuccess={() => { setWelcomeFlow(null); window.location.reload(); }} />);
    if (welcomeFlow === 'privacy') return (<PrivacyPolicy onBack={() => setWelcomeFlow(null)} />);
    if (welcomeFlow === 'about') return (<About onBack={() => setWelcomeFlow(null)} />);
    return <Auth />;
  }

  if (!projetoId) {
    if (welcomeFlow === 'create') return (<CreateProjectFlow onBack={() => setWelcomeFlow(null)} onSuccess={() => { setWelcomeFlow(null); window.location.reload(); }} />);
    if (projetoErro) return fullScreen(
      <div className="max-w-md w-full text-center bg-white p-8 rounded-xl border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('loadingProject.errorTitle')}</h2>
        <p className="text-sm text-gray-500 mb-6">{t('loadingProject.errorBody')}</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={inicializarProjeto}>{t('common.retry')}</Button>
          <Button variant="secondary" onClick={handleLogout}>{t('noProject.logoutButton')}</Button>
        </div>
      </div>
    );
    if (projetoId === false) return fullScreen(
      <div className="max-w-md w-full text-center bg-white p-8 rounded-xl border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('noProject.title')}</h2>
        <p className="text-sm text-gray-500 mb-6">{t('noProject.body')}</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button onClick={() => setWelcomeFlow('create')}>{t('noProject.createButton')}</Button>
          <Button variant="secondary" onClick={handleLogout}>{t('noProject.logoutButton')}</Button>
        </div>
      </div>
    );
    return fullScreen(
      <div className="max-w-md w-full text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('loadingProject.title')}</h2>
        <p className="text-sm text-gray-500">{t('loadingProject.body')}</p>
      </div>
    );
  }

  const navItems = [
    { id: 'dashboard', label: t('nav.dashboard'), icon: Home },
    { id: 'membros', label: t('nav.members'), icon: Users },
    { id: 'tarefas', label: t('nav.tasks'), icon: Clock },
    { id: 'insights', label: t('nav.insights'), icon: Sparkles },
    { id: 'config', label: t('nav.config'), icon: Settings }
  ];

  const modalActions = { adicionarMembro, editarMembro, adicionarTarefa, editarTarefa, registrarAtividade, atualizarRegistro };
  const nomeExibido = perfil.nome || (session.user.email || '').split('@')[0];
  const userMenu = [
    { label: t('nav.config'), icon: Settings, onClick: () => { setCurrentView('config'); setMobileMenuOpen(false); } },
    { label: t('nav.logout'), icon: LogOut, onClick: handleLogout, tone: 'danger' },
  ];

  const brand = (compact = false) => (
    <div className="flex items-center gap-2 min-w-0">
      <img src="/logo.png" alt="" className={compact ? 'h-6 w-auto' : 'h-7 w-auto'} />
      <span className="font-semibold text-gray-900 truncate">Dojo Mojo</span>
    </div>
  );

  return (
    <div className="min-h-screen flex overflow-x-clip">
      {notice && (
        <div
          role={notice.isError ? 'alert' : 'status'}
          className={cx('fixed top-4 left-1/2 -translate-x-1/2 z-[70] max-w-md w-[calc(100%-2rem)] px-4 py-3 rounded-lg shadow-pop text-sm font-medium', notice.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-900 text-white')}
        >
          {notice.text}
        </div>
      )}

      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-gray-900/40 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      <aside className={cx('fixed top-0 left-0 h-screen w-64 bg-gray-50 border-r border-gray-200 z-50 flex flex-col transition-transform duration-200', mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0')}>
        <div className="h-14 px-4 flex items-center justify-between border-b border-gray-200">
          {brand()}
          <IconButton icon={X} label={t('common.close')} onClick={() => setMobileMenuOpen(false)} className="lg:hidden" />
        </div>

        {projeto && (
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 min-w-0">
            <Avatar src={projeto.avatar_url} name={projeto.nome} color="#6A51A3" size="sm" square />
            <div className="min-w-0">
              <h2 className="text-sm font-medium text-gray-900 truncate">{projeto.nome}</h2>
              {projeto.descricao && (<p className="text-xs text-gray-500 truncate">{projeto.descricao}</p>)}
            </div>
          </div>
        )}

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setMobileMenuOpen(false);
                }}
                aria-current={isActive ? 'page' : undefined}
                className={cx('w-full flex items-center gap-2.5 px-2.5 h-9 rounded-md text-sm font-medium transition-colors', isActive ? 'bg-gray-200/70 text-gray-900' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')}
              >
                <Icon size={16} className={isActive ? 'text-gray-900' : 'text-gray-400'} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-200">
          <Menu
            items={userMenu}
            align="left"
            trigger={({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                <Avatar src={perfil.avatar_url} name={nomeExibido} color="#6A51A3" size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{nomeExibido}</div>
                  <div className="text-xs text-gray-500 truncate">{session.user.email}</div>
                </div>
                <ChevronsUpDown size={14} className="text-gray-400 flex-shrink-0" />
              </button>
            )}
          />
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen min-w-0 overflow-x-clip lg:ml-64">
        <header className="lg:hidden h-14 bg-white border-b border-gray-200 sticky top-0 z-30 px-3 flex items-center justify-between">
          <IconButton icon={MenuIcon} label={t('nav.openMenu')} size={20} onClick={() => setMobileMenuOpen(true)} />
          {brand(true)}
          <button type="button" onClick={() => setCurrentView('config')} aria-label={t('nav.account')} className="rounded-full">
            <Avatar src={perfil.avatar_url} name={nomeExibido} color="#6A51A3" size="sm" />
          </button>
        </header>

        <main className="flex-1 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
          {loadError && (
            <Notice tone="error" className="mb-6" action={<Button size="sm" variant="secondary" onClick={carregarDados}>{t('common.retry')}</Button>}>
              {t('errors.loadFailed')}
            </Notice>
          )}
          {!dataLoaded && currentView !== 'config' ? (
            !loadError && <p className="text-center py-20 text-sm font-medium text-gray-500">{t('common.loading')}</p>
          ) : (
            <>
              {currentView === 'dashboard' && (
                <Dashboard
                  data={data}
                  projeto={projeto}
                  perfil={perfil}
                  onNewEntry={() => openModal('registrar')}
                  onEditEntry={(registro) => openModal('registrar', registro)}
                  onDeleteEntry={removerRegistro}
                  onNavigate={setCurrentView}
                />
              )}
              {currentView === 'membros' && (
                <Membros
                  membros={data.membros}
                  onNew={() => openModal('membro')}
                  onEdit={(membro) => openModal('membro', membro)}
                  onDelete={removerMembro}
                />
              )}
              {currentView === 'tarefas' && (
                <Tarefas
                  tarefas={data.tarefas}
                  categorias={data.categorias}
                  onNew={() => openModal('tarefa')}
                  onEdit={(tarefa) => openModal('tarefa', tarefa)}
                  onDelete={removerTarefa}
                />
              )}
              {currentView === 'insights' && <Insights data={data} />}
              {currentView === 'config' && (
                <Config
                  session={session}
                  projetoId={projetoId}
                  perfil={perfil}
                  onPerfilChange={(mudancas) => setPerfil(prev => ({ ...prev, ...mudancas }))}
                  onProjetoChange={(mudancas) => setProjeto(prev => ({ ...prev, ...mudancas }))}
                  onArchive={arquivarHistorico}
                  onLogout={handleLogout}
                />
              )}
            </>
          )}
        </main>
      </div>

      {modal && (
        <FormModal
          type={modal.type}
          item={modal.item}
          data={data}
          defaultMembroId={defaultMembroId}
          actions={modalActions}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
